import type { PlanTier } from "@/types";
import type { TranslationKey } from "@/lib/i18n/locales";
import {
  ANNUAL_SAVINGS_PERCENT,
  getAnnualSavingsPercent,
  GO_PRICE_USD,
  MAXPLUS_PRICE_USD,
  MAX_PRICE_USD,
  PRO_PRICE_USD,
  type BillingPlanId,
  type PlanId,
} from "@/lib/billing-plans";

export {
  ANNUAL_SAVINGS_PERCENT,
  getAnnualSavingsPercent,
  type BillingPlanId,
  type PlanId,
};

export interface PlanConfig {
  tier: PlanTier;
  label: string;
  requestsPerMinute: number;
  /** Caracteres maximos por solicitud individual (SSOT — unico enforcement
   * de chars; la quota diaria de chars se retiro 2026-10-01 D1: declarada
   * pero nunca cobrada — unico enforcement era charsPerRequest en
   * pipeline/index.ts:855 y el usado-del-dia leia un bucket sin escritor;
   * research 2026 reserve-commit-release: quota sin writer es display muerto) */
  charsPerRequest: number;
  /** Maximo de chats guardados en historial */
  maxSavedTexts: number;
  /** Precio público en USD (número, 0 = gratis) — sourced from billing-plans */
  priceUsd: number;
  /** Requests de IA por ventana de 24h — contador general (Fast + Auto→Fast). */
  aiRequestsPerDay: number;
  /** Requests Smart por día (cuota separada 2026-09-18): los turnos Smart
   * (modo smart o auto→smart) consumen 1 de esta cuota ADEMÁS de la general.
   * Protege el pool flash (5 RPM/20 RPD por key): quien quema Smart no quema
   * el resto. Free = 0 (solo Fast). SSOT PLAN_CONFIG. */
  aiSmartRequestsPerDay: number;
  /** Tokens estimados por día para Chat Studio (AI token budget) — BPE exacto */
  aiTokensPerDay: number;
  /** Tokens estimados por ventana semanal rodante 7×24h (anti-burst 2026-10-06,
   * decisión dueño "mejor camino": 5× el diario — un burst de 5 días a tope
   * agota la semana aunque cada día respete su cap. Ventana rodante (reset =
   * now+7d por bucket, SSOT usage-tracker.ts nextWeeklyResetAt), no semana
   * calendario. Fail-closed: sin reserva semanal no hay turno. Cuota de
   * COSTE/protección como pythonFullPerDay: fuera de la tabla display (sin
   * columna Pricing/matriz, sin copy, sin clave i18n) — documentada en
   * docs/INFRASTRUCTURE.md §3 "fuera de la tabla" + audit:plans (checks +
   * literales + pin documental). SSOT PLAN_CONFIG. */
  aiTokensWeekly: number;
  /** Tokens máximos por request individual en Chat Studio */
  aiTokensPerRequest: number;
  /** Trial Smart lifetime (2026-09-26, gancho Free→Go): turnos Smart DE POR
   * VIDA por usuario free (no diario). Solo free > 0; go/pro/max/maxplus = 0
   * (usan su cuota diaria aiSmartRequestsPerDay). Contador en
   * app_metadata.smart_trial_used vía service client (sin migración SQL).
   * Protege el pool Flash (familia Flash 320 RPD = 160×2): 3 lifetime por
   * free acota el burn a 3 smarts por cuenta. SSOT PLAN_CONFIG. */
  smartTrialTotal: number;
  /** Maximo de skills activas simultáneamente por mensaje */
  maxActiveSkills: number;
  /** Imágenes generadas por IA permitidas por día (privilegio del plan). */
  imageGenerationsPerDay: number;
  /** Imágenes generadas que el usuario puede guardar en su biblioteca local. */
  maxSavedImages: number;
  // ── Vortixy Study (Work-parity, 2026 research) ─────────────────────
  /** Espacios de estudio (Projects) máximos — free 1, go 3, pro 8, max 15, maxplus 25 — finitos, TTL aparte */ // RESUELTO 2026-09-12 [E-P2] copy highest/ilimitado|fuente SSOT src/lib/plans.ts:PLAN_CONFIG
  maxStudyProjects: number;
  /** Archivos por espacio (knowledge base) — igual que industry pattern 5/25/40; cada archivo 20 MiB Storage (chat-artifacts) pero DB solo guarda extractedText 20k chars (~20KB/fila) */
  maxFilesPerStudyProject: number;
  /** Tareas programadas activas (Scheduled Tasks) — SSOT PLAN_CONFIG: Free 1, Go 2, Pro 5, Max 10, Max+ 15 */
  maxStudyTasks: number;
  /** Planes de estudio guardados (2026-10-21) — tope TOTAL por plan, no
   * diario: se libera borrando un plan. `POST /api/study/plans/generate`
   * cuenta con `count: "exact", head: true` (fail-closed) antes de reservar
   * tokens/rate-limit y responde 403 con `studyPlansQuotaExceeded`; el hub
   * avisa cuando la página no muestra todos. SSOT PLAN_CONFIG. */
  maxStudyPlans: number;
  /** Generaciones Modo Examen (flashcards/quiz) por día — deep research style */
  studyGenerationsPerDay: number;
  /** Búsquedas web Tavily por día — protege pool global 4k/mes de DoS por un solo usuario */
  tavilySearchesPerDay: number;
  /** Artefactos de chat (PDF/DOCX originales subidos a Storage) por día — evita llenar bucket 1GB gratis */
  chatArtifactsPerDay: number;
  /** Exportaciones Word/PDF por día — protege CPU serverless */
  exportPerDay: number;
  /** Python completo (Modal) por día — full primero, subset local fallback.
   * SSOT PLAN_CONFIG. Sin claves Modal → 0 efectivo (todo local, sin error). */
  pythonFullPerDay: number;
  /** Audios de repaso (Modo Examen → Escuchar) por día — 2026-10-07. Coste:
   * Cloudflare Workers AI TTS (comparte los 10k Neurons/día por cuenta con las
   * imágenes). SSOT PLAN_CONFIG: Free 1, Go 3, Pro 8, Max 15, Max+ 25. */
  studyAudioPerDay: number;
  /** Importaciones de transcripción de YouTube (Modo Examen) por día —
   * 2026-10-07. Coste: pool de proveedores de transcripción (cupo mensual
   * gratis). SSOT PLAN_CONFIG: Free 1, Go 3, Pro 8, Max 15, Max+ 25. */
  youtubeImportsPerDay: number;
}

// 2026-08-29 plan reduction — justo con lo que la gente paga (research 2026-08-29: industry pattern Free 10 req/d, Go 30 → Vortixy ajusta -40% para margen y sostenibilidad)
// 2026-08-29: Free 5 req/d (vs 10) mantiene 750 DAU con 7.5k RPD pero reduce abuso; Go $4 20 req sigue 2× más barato que industria Go $8; Pro $8 50 req 2.5× más barato que industria Plus $20; Max 100 req mantiene margen.
// Tokens, imágenes, proyectos y tareas reducidos proporcional -40% para fair value, Tavily ajustado para proteger pool 3k créditos/mes (1.5k advanced).
// RESUELTO 2026-09-12 [GENERAL] pool 8k→3k (recorte anti-ban 8→3 keys) |fuente src/lib/ai-engine/search-pool.ts:1-17
// 2026-09-22 cuota UX x2 (reporte Free/Go se agotan en 1-2 preguntas: reserva sobre historial completo + system ~5k dejaba ~3.5k útiles; websearch 2026-09-22 "2026 LLM quota UX token reservation compaction" sin resultados en integración — fallback a evidencia local + patrón industry effective-window/reserve-commit-release): tokens/día y req/día x2 en los 5 planes, per-request intacto (ventana contexto sin cambio, trigger 85% estable). SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
export const PLAN_CONFIG: Record<PlanTier, PlanConfig> = {
  free: {
    tier: "free",
    label: "Free",
    requestsPerMinute: 10,
    charsPerRequest: 10_000,
    maxSavedTexts: 30,
    priceUsd: 0,
    aiRequestsPerDay: 15,
    aiSmartRequestsPerDay: 0,
    // 2026-09-16 anti-compactación Free/Go: el trigger de compactación es 85%
    // del budget (context-budget.ts triggerFor). Con system prompt ~5k, 10k/req
    // dejaba ~3.5k útiles → compactaba cada rato. 16k/req da headroom real
    // (trigger 13.6k). Daily 24k para ratio daily/per-request 24/16=1.5.
    // 2026-09-22 cuota UX x2 (SSOT PLAN_CONFIG): 24k→48k tokens/día, 5→10 req/día.
    // Per-request 16k intacto (ventana contexto estable). // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    // 2026-09-27 subida Free/Go/Pro (requisito dueño; websearch 2026-09-27
    // "2026 LLM quota UX token reservation compaction context window":
    // atlan 2026 context-rot/effective-window + redis 2026 overflow + neuraltrust
    // 2026 turn-summarization — la reserva BPE sobre historial completo + system
    // ~5k dejaba ~12k útiles con 20k/req y compactaba cada rato; 30k/req da
    // trigger 85% → 25.5k de headroom real sin tocar el modelo/ventana):
    // 72k→100k tokens/día, 20k→30k/req.
    // Escalera intacta: 100k < Go 200k < Pro 300k < Max 500k < Max+ 1000k.
    // // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    // 2026-10-15 requisito dueño (incidente real uiuxchatbot@gmail.com: el
    // turno Nº1 de un Free reservó 30.219 tokens — system prompt medido en
    // vivo ~19.8k BPE (79k chars, chat-system-prompt.ts) + skill candidata
    // ~10k + mensaje — contra el techo 30k: 429 tokens_per_request, la fila
    // user se compensó y el chat quedó fantasma. El techo por turno cubre
    // system + skills + historial: con 30k el plan NUNCA cabía (bug P0 de
    // producto, no de uso). 30k→64k/req (system ~20k + ~44k de historial) y
    // 100k→200k/día (15 req/día reales: ~10 turnos típicos de ~20k; antes
    // 100k/día ≈ 3-5 y los usuarios chocaban en la primera sesión).
    // // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    aiTokensPerDay: 200_000,
    aiTokensWeekly: 1_000_000,
    aiTokensPerRequest: 64_000,
    smartTrialTotal: 3,
    maxActiveSkills: 2,
    imageGenerationsPerDay: 2,
    maxSavedImages: 8,
    maxStudyProjects: 1,
    maxFilesPerStudyProject: 3,
    maxStudyTasks: 1,
    maxStudyPlans: 1,
    studyGenerationsPerDay: 2,
    tavilySearchesPerDay: 10,
    chatArtifactsPerDay: 3,
    exportPerDay: 20,
    pythonFullPerDay: 2,
    studyAudioPerDay: 1,
    youtubeImportsPerDay: 1,
  },
  // 2026-08-29 Go $4 — 2× más barato que industry pattern Go $8, 20 req vs 160/3h rival (ajustado -33%)
  go: {
    tier: "go",
    label: "Go",
    requestsPerMinute: 15,
    charsPerRequest: 15_000,
    maxSavedTexts: 100,
    priceUsd: GO_PRICE_USD,
    aiRequestsPerDay: 60,
    aiSmartRequestsPerDay: 6,
    // 2026-09-16 anti-compactación Free/Go: 20k/req → trigger 17k, muy justo con
    // system prompt ~5k + historial. 32k/req da trigger 27.2k con headroom real.
    // Daily 80k mantiene ratio daily/per-request 80/32=2.5 igual que antes.
    // 2026-09-22 cuota UX x2 (SSOT PLAN_CONFIG): 80k→160k tokens/día, 20→40 req/día.
    // Per-request 32k intacto. // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    // 2026-09-27 requisito dueño: per-request 32k→30k (paridad con Free 30k;
    // la diferenciación $4→$8 vive en daily 200k vs 100k + Smart 6 + 60 req/d,
    // no en el techo por turno). Daily 200k INTACTO. Trigger 85% → 25.5k de
    // headroom (system ~5k). Ratio daily/per-req 200/30≈6.7: más turnos por
    // día, mismo techo de contexto por turno. Flash intacto (Smart Go 6 sin
    // cambio). Escalera: Free 100k < 200k < Pro 300k < Max 500k.
    // // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    // 2026-10-15 requisito dueño (mismo incidente P0 que Free): 30k/req no
    // cubría system (~20k) + skill + historial → 64k/req (paridad de techo por
    // turno con Free, la diferenciación sigue en daily/Smart/req/día); daily
    // 200k→400k (2×. Escalera coherente: 200k < 400k < 600k < 1M < 2M).
    // // SSOT PLAN_CONFIG — no hardcodear fuera de aquí.
    aiTokensPerDay: 400_000,
    aiTokensWeekly: 2_000_000,
    aiTokensPerRequest: 64_000,
    smartTrialTotal: 0,
    maxActiveSkills: 3,
    imageGenerationsPerDay: 8,
    maxSavedImages: 15,
    maxStudyProjects: 3,
    maxFilesPerStudyProject: 8,
    maxStudyTasks: 2,
    maxStudyPlans: 3,
    studyGenerationsPerDay: 5,
    tavilySearchesPerDay: 25,
    chatArtifactsPerDay: 10,
    exportPerDay: 30,
    pythonFullPerDay: 5,
    studyAudioPerDay: 3,
    youtubeImportsPerDay: 3,
  },
  pro: {
    tier: "pro",
    label: "Pro",
    requestsPerMinute: 20,
    charsPerRequest: 25_000,
    maxSavedTexts: 300,
    priceUsd: PRO_PRICE_USD,
    aiRequestsPerDay: 100,
    aiSmartRequestsPerDay: 15,
    // 2026-10-04 requisito dueño: 35k→40k tokens/petición en Pro.
    // 2026-10-15 requisito dueño (coherencia de escalera tras el bump Free/Go
    // del incidente P0): 40k→128k/req (techo holgado por turno; el gasto real
    // lo acota el daily) y 300k→600k/día (2×, ratio 2/4/6/10/20 × 100k).
    aiTokensPerDay: 600_000,
    aiTokensWeekly: 3_000_000,
    aiTokensPerRequest: 128_000,
    smartTrialTotal: 0,
    maxActiveSkills: 5,
    imageGenerationsPerDay: 30,
    maxSavedImages: 30,
    maxStudyProjects: 8,
    maxFilesPerStudyProject: 15,
    maxStudyTasks: 5,
    maxStudyPlans: 8,
    studyGenerationsPerDay: 12,
    tavilySearchesPerDay: 50,
    chatArtifactsPerDay: 25,
    exportPerDay: 50,
    pythonFullPerDay: 12,
    studyAudioPerDay: 8,
    youtubeImportsPerDay: 8,
  },
  max: {
    tier: "max",
    label: "Max",
    requestsPerMinute: 30,
    charsPerRequest: 32_000,
    maxSavedTexts: 400,
    priceUsd: MAX_PRICE_USD,
    aiRequestsPerDay: 200,
    aiSmartRequestsPerDay: 30,
    // 2026-09-22 cuota UX x2 (SSOT PLAN_CONFIG): 250k→500k tokens/día, 100→200 req/día. Per-request 50k intacto.
    // 2026-10-15 requisito dueño (coherencia de escalera): 50k→192k/req,
    // 500k→1M/día (2×, misma proporción dentro de la escalera 2/4/6/10).
    aiTokensPerDay: 1_000_000,
    aiTokensWeekly: 5_000_000,
    aiTokensPerRequest: 192_000,
    smartTrialTotal: 0,
    maxActiveSkills: 12,
    imageGenerationsPerDay: 60,
    maxSavedImages: 80,
    maxStudyProjects: 15,
    maxFilesPerStudyProject: 30,
    maxStudyTasks: 10,
    maxStudyPlans: 15,
    studyGenerationsPerDay: 20,
    // 2026-09-12 loss-proof: 100/día × 3 usuarios vaciaban el pool global
    // (~12.5k/mes cadena 6 proveedores, SSOT docs/INFRASTRUCTURE.md §2).
    // 2026-09-26 corrección estable: 50→60 — Pro también tenía 50 (cero
    // diferenciación $8→$14) y 60/mes-max 1800 ≈ 14% de la cadena; el techo
    // alto sigue en Max+ (75). // SSOT PLAN_CONFIG.
    tavilySearchesPerDay: 60,
    chatArtifactsPerDay: 60,
    exportPerDay: 100,
    pythonFullPerDay: 20,
    studyAudioPerDay: 15,
    youtubeImportsPerDay: 15,
  },
  // 2026-10-16 Max+ $24 — cuotas diarias dobles de Max a $24/mes (toggle 1x/2x en pricing). Tope consciente.
  // 2026-09-26 corrección estable: 100→75 — un usuario a tope quemaba 3000
  // búsquedas/mes ≈ 24% de la cadena global (~12.5k/mes); 75 deja el techo
  // en 2250/mes ≈ 18% con 4 usuarios heavy antes de saturar (antes 3).
  // Sigue neto top (75 > 60 Max > 50 Pro). // SSOT PLAN_CONFIG.
  maxplus: {
    tier: "maxplus",
    label: "Max+",
    requestsPerMinute: 60,
    charsPerRequest: 64_000,
    maxSavedTexts: 800,
    priceUsd: MAXPLUS_PRICE_USD,
    aiRequestsPerDay: 400,
    aiSmartRequestsPerDay: 60,
    // 2026-09-22 cuota UX x2 (SSOT PLAN_CONFIG): 500k→1M tokens/día, 200→400 req/día. Per-request 100k intacto.
    // 2026-10-15 requisito dueño (coherencia de escalera): 100k→256k/req,
    // 1M→2M/día (2×; el tope consciente sigue siendo la cima de la escalera).
    aiTokensPerDay: 2_000_000,
    aiTokensWeekly: 10_000_000,
    aiTokensPerRequest: 256_000,
    smartTrialTotal: 0,
    maxActiveSkills: 12,
    // 2026-10-01 D2 requisito dueño: Max+ 100→95 imgs/día (el techo Cloudflare
    // free 10k Neurons/día ≈ 95 imgs/día a 104.2 Neurons/img — un Max+ a tope
    // ya no puede quemar solo el pool diario compartido; escalera intacta:
    // Free 2 < Go 8 < Pro 30 < Max 60 < Max+ 95). Superficies que muestran el
    // 100 de Max+ (docsImagesVariantsHint ×14, planMaxplusFeature5 ×14,
    // section-models fallback, SKILL.md, INFRA) van a 95 en el mismo cambio.
    // 2026-10-06 VERTICAL A OFERTA (decisión dueño "mejor camino": un Max+ a
    // 95 satura el pool Cloudflare 10k Neurons con un solo heavy — TinyFish
    // 2026 per-user quota shared pool: Gemini Enterprise 7-day rolling shared
    // pool + SNE consumption caps/overage protection): Max+ 95→60 imgs/día
    // (serie 2/8/30/60/60; Max y Max+ comparten techo de imágenes, la
    // diferenciación $9→$18 vive en tokens/req/smart/tavily). Mismas superficies
    // que el cambio D2 van a 60 en este cambio (i18n/docs/llms/INFRA/README/
    // AGENTS + audits). El techo físico del pool por cuenta (~95) NO cambia.
    imageGenerationsPerDay: 60,
    maxSavedImages: 120,
    maxStudyProjects: 25,
    maxFilesPerStudyProject: 50,
    maxStudyTasks: 15,
    maxStudyPlans: 25,
    studyGenerationsPerDay: 30,
    tavilySearchesPerDay: 75,
    chatArtifactsPerDay: 100,
    exportPerDay: 150,
    pythonFullPerDay: 30,
    studyAudioPerDay: 25,
    youtubeImportsPerDay: 25,
  },
};

const ACCOUNT_PLAN_TIERS = [
  "free",
  "go",
  "pro",
  "max",
  "maxplus",
] as const satisfies readonly PlanTier[];
type AccountPlanTier = (typeof ACCOUNT_PLAN_TIERS)[number];

/** Limite global absoluto del servidor (validacion final) */
// 2026-08-26 HARD_CHAR_CAP — 64k absolute server cap, plan-aware 10k/15k/25k/32k/64k (maxplus)
// RESUELTO 2026-09-13 [A-CRIT] tope 32k→64k end-to-end: Max+ promete 64k/request (plans.ts:149) pero Zod topaba en 32k|fuente src/lib/plans.ts:149
// 2026-10-13 hallazgo 1 pin rango muerto (SSOT PLAN_CONFIG manda, sin hardcodear
// quotas fuera): HARD_CHAR_CAP == PLAN_CONFIG.maxplus.charsPerRequest por
// construcción — no hay rango muerto por encima del plan máximo (un cap mayor
// prometería lo inalcanzable, uno menor recortaría Max+ en silencio). El pin
// vivo está en audit:plans (paridad CAP == maxplus); este comentario es el
// mapeo documental, no un segundo origen.
export const HARD_CHAR_CAP = 64_000;

export function isAccountPlanTier(value: unknown): value is AccountPlanTier {
  return (
    typeof value === "string" &&
    (ACCOUNT_PLAN_TIERS as readonly string[]).includes(value)
  );
}

/**
 * Plan efectivo único (P2-3, 2026-09-30 FASE 2 fixer-db-auth).
 * Misma regla en los 3 sitios que la triplicaban (plans.ts resolvePlanTier,
 * chat-cleanup resolveEffectivePlan + gallery, downgrade getDowngradeInfo):
 * disputed → free; expirado (nowMs > subscription_end_date) → free;
 * en otro caso el plan crudo (incluido paid sin end_date: app_metadata solo
 * la escribe service_role — ver admin-user-updates.ts — luego un paid sin
 * fecha es escritura nuestra, no forja del usuario; las rutas destructivas
 * (TTL/downgrade) retienen en vez de borrar ante metadata incompleta y el
 * abuso de cuotas es imposible sin service_role).
 * Research 2026: AxonBuild 2026-08-21 (el estado ended/disputed del proveedor
 * debe reflejarse en el access record local; ante fallo de resolución no se
 * concede acceso) + TechResolve 2026-03-04 (disputa activa → reconciliación
 * y grace antes de revocar). Desconocido total (sin metadata resuelta) NO es
 * free: los callers difieren fail-closed (chat-cleanup unknownPlanSkipped).
 */
export function getEffectivePlan(
  appMetadata: Record<string, unknown> | null | undefined,
  nowMs: number = Date.now(),
): PlanTier {
  const raw =
    typeof appMetadata?.plan === "string"
      ? appMetadata.plan.toLowerCase()
      : "free";
  if (!isAccountPlanTier(raw)) return "free";
  if (raw === "go" || raw === "pro" || raw === "max" || raw === "maxplus") {
    const status = appMetadata?.subscription_status;
    if (status === "disputed") return "free";
    const endDate = Number(appMetadata?.subscription_end_date ?? 0);
    if (endDate > 0 && nowMs > endDate) return "free";
  }
  return raw;
}

export function resolvePlanTier(
  user:
    | {
        email?: string | null;
        app_metadata?: Record<string, unknown>;
      }
    | null
    | undefined,
  adminEmail?: string | null,
): PlanTier {
  if (!user) return "free";

  if (
    adminEmail &&
    user.email &&
    user.email.toLowerCase() === adminEmail.toLowerCase()
  ) {
    return "maxplus";
  }

  const raw = user.app_metadata?.plan;
  const normalized = typeof raw === "string" ? raw.toLowerCase() : raw;
  if (isAccountPlanTier(normalized)) {
    // P2-3 (2026-09-30): la regla disputed/expirado vive en getEffectivePlan
    // (única) — aquí solo se delega tras el override de admin.
    return getEffectivePlan(
      user.app_metadata as Record<string, unknown> | undefined,
    );
  }

  return "free";
}

export function getPlanConfig(plan: PlanTier): PlanConfig {
  return PLAN_CONFIG[plan];
}

/** i18n key del nombre visible del plan (SSOT display — dashboard/pricing). */
export function planNameKey(plan: string | null | undefined): TranslationKey {
  if (plan === "maxplus") return "planMaxplusName";
  if (plan === "max") return "planMaxName";
  if (plan === "pro") return "planProName";
  if (plan === "go") return "planGoName";
  return "planFreeName";
}

/** true para cualquier plan de pago (go/pro/max/maxplus). */
export function isPaidPlanTier(plan: string | null | undefined): boolean {
  return (
    plan === "go" || plan === "pro" || plan === "max" || plan === "maxplus"
  );
}

/** ms restantes hasta el próximo reseteo de cuota diaria (00:00 UTC) */
export function msUntilDailyReset(now: Date = new Date()): number {
  const next = new Date(now);
  next.setUTCHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
}

/** Valores del plan Free para interpolar `faq2A` (`{requests}` `{tokens}`
 * `{searches}` `{images}` `{exam}` `{audio}` `{youtube}` `{exports}`).
 * SSOT PLAN_CONFIG (nunca literales): tokens en formato "200k" (serie
 * 200k/400k/600k/1000k/2000k desde 2026-10-15). Lo usan FAQ.tsx y el FAQPage schema de
 * src/app/page.tsx — un solo origen, sin drift. */
export function getFreePlanFaqValues(): Record<string, string> {
  const free = PLAN_CONFIG.free;
  return {
    requests: String(free.aiRequestsPerDay),
    tokens: `${free.aiTokensPerDay / 1000}k`,
    searches: String(free.tavilySearchesPerDay),
    images: String(free.imageGenerationsPerDay),
    exam: String(free.studyGenerationsPerDay),
    audio: String(free.studyAudioPerDay),
    youtube: String(free.youtubeImportsPerDay),
    exports: String(free.exportPerDay),
  };
}
